import type {
  Improvements,
  OrderFeature,
  OrderFeatureDefinition,
  SelectedSources,
} from '@aloft/toolkit-shared';
import {
  EXPLICIT_UNKNOWN_SOURCE_DISPLAY,
  ORDER_FEATURES,
  isExplicitUnknownSelectedForFieldPath,
} from '@aloft/toolkit-shared';

import type { ImprovementsPathPrefix } from '../../tkv2PDF/utils/utils.ts';
import { resolveOrderKeyedFeaturePdfCell } from '../../tkv2PDF/utils/utils.ts';

export type AdjustmentsFeatureDisplayContext = {
  isArv?: boolean;
};

const improvementsPathPrefix = (isArv?: boolean): ImprovementsPathPrefix =>
  isArv ? 'editableData.arv.improvements' : 'editableData.improvements';

export const resolveAdjustmentsFeatureDisplay = ({
  improvements,
  orderFeatures,
  selectedSources,
  isArv,
  orderKeys,
  featureDefs,
  unknownPathSuffixes,
  format,
}: {
  improvements?: Improvements;
  orderFeatures?: OrderFeature[];
  selectedSources?: SelectedSources;
  isArv?: boolean;
  orderKeys: readonly OrderFeature[];
  featureDefs: readonly OrderFeatureDefinition[];
  unknownPathSuffixes?: readonly string[];
  format: () => string;
}): string | undefined => {
  if (!improvements) return undefined;

  const prefix = improvementsPathPrefix(isArv);
  const result = resolveOrderKeyedFeaturePdfCell(improvements, orderFeatures, {
    orderKeys,
    featureDefs,
    unknownEarly: () =>
      unknownPathSuffixes?.some((suffix) =>
        isExplicitUnknownSelectedForFieldPath(selectedSources, `${prefix}.${suffix}`),
      )
        ? EXPLICIT_UNKNOWN_SOURCE_DISPLAY
        : undefined,
    format,
  });

  return result || undefined;
};

export const isBasementTreatAsNone = (
  improvements?: Improvements,
  orderFeatures?: OrderFeature[],
): boolean => {
  if (!improvements) return false;
  return (
    improvements.basement?.hasFeature === false ||
    (orderFeatures !== undefined &&
      orderFeatures.includes('basement') &&
      improvements.basement?.hasFeature !== true)
  );
};

/**
 * The Finished Area cell is also treated as 'None' when the basement Type is
 * 'unfinished', regardless of whether a finishedArea value happens to be populated.
 */
export const isBasementFinishedAreaTreatAsNone = (
  improvements?: Improvements,
  orderFeatures?: OrderFeature[],
): boolean =>
  isBasementTreatAsNone(improvements, orderFeatures) ||
  improvements?.basement?.type === 'unfinished';

/**
 * The Total Area cell is only treated as 'None' when the basement feature itself is absent.
 * When Type is 'finished', `normalizeBasementAreas` (see @aloft/toolkit-shared) already keeps
 * `totalArea` equal to `finishedArea`, so the cell should display that mirrored value rather than
 * being forced to 'None'.
 */
export const isBasementTotalAreaTreatAsNone = (
  improvements?: Improvements,
  orderFeatures?: OrderFeature[],
): boolean => isBasementTreatAsNone(improvements, orderFeatures);

export const garageCarportFeatureDefs = [
  ORDER_FEATURES.carport,
  ORDER_FEATURES.detachedGarage,
  ORDER_FEATURES.garage,
] as const;

export const porchPatioDeckFeatureDefs = [
  ORDER_FEATURES.porch,
  ORDER_FEATURES.patio,
  ORDER_FEATURES.deck,
] as const;

export const poolSpaFeatureDefs = [ORDER_FEATURES.pool, ORDER_FEATURES.spa] as const;

export const shouldDisplayAdjustmentsFeature = ({
  featureDependencies,
  orderFeatures,
  improvementEntities,
}: {
  featureDependencies: readonly OrderFeature[];
  orderFeatures?: OrderFeature[];
  improvementEntities: Record<string, Improvements | undefined>;
}): boolean =>
  featureDependencies.some(
    (feature) =>
      orderFeatures?.includes(feature) ||
      Object.values(improvementEntities).some(
        (improvements) =>
          improvements != null &&
          (ORDER_FEATURES[feature].isPresent(improvements) ||
            ORDER_FEATURES[feature].hasFeature(improvements) ||
            ORDER_FEATURES[feature].isNone(improvements)),
      ),
  );
